if (!customElements.get('sticky-atc-bar')) {
  customElements.define(
    'sticky-atc-bar',
    class StickyAtcBar extends HTMLElement {
      constructor() {
        super();
        document.body.classList.add('sticky-atc-bar-enabled');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get productId() {
        return this.dataset.productId;
      }

      connectedCallback() {
        this.container = this.closest('.sticky-atc-bar');
        this.mainProductInfo = document.querySelector(`product-info[data-product-id="${this.productId}"]`);
        this.mainProductForm = document.querySelector(`.main-product-form[data-product-id="${this.productId}"]`);
        this.mainAddToCartBlock = this.mainProductInfo?.querySelector('.product__block--pdp_add_to_cart');
        this.mainAddToCartButton = this.mainAddToCartBlock?.querySelector('.pdp-add-to-cart__button');
        this.mainNativeAtcButton = this.mainProductInfo?.querySelector('[id^="ProductSubmitButton-"]');
        this.stickyEnabledMarker = this.mainProductInfo?.querySelector('[data-pdp-sticky-atc-enabled]');

        if (!this.mainProductInfo || !this.stickyEnabledMarker) {
          this.disableSticky();
          return;
        }

        this.productForm = this.querySelector('form[is="product-form"]');
        if (!this.productForm) {
          this.disableSticky();
          return;
        }

        this.enableSticky();

        this.variantInput = this.productForm.querySelector('input[name="id"]');
        this.quantityInput = this.productForm.querySelector('input[name="quantity"]');
        this.sellingPlanInput = this.productForm.querySelector('input[name="selling_plan"]');
        this.submitButton = this.productForm.querySelector('button[name="add"]');
        this.submitButtonText = this.productForm.querySelector('.sticky-atc-bar__submit-text');

        this.selectorButton = this.querySelector('[data-sticky-selector]');
        this.dropdown = this.querySelector('[data-sticky-dropdown]');
        this.dropdownOptions = this.querySelector('[data-sticky-options]');
        this.titleMainEl = this.querySelector('[data-sticky-title-main]');
        this.titleSaveEl = this.querySelector('[data-sticky-title-save]');
        this.subtitleEl = this.querySelector('[data-sticky-subtitle]');

        this.handleSelectorClick = this.handleSelectorClick.bind(this);
        this.handleDropdownOptionClick = this.handleDropdownOptionClick.bind(this);
        this.handleDocumentClick = this.handleDocumentClick.bind(this);
        this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this);
        this.handleFormSubmit = this.handleFormSubmit.bind(this);
        this.handleMainPlanChange = this.handleMainPlanChange.bind(this);
        this.handleMainPurchaseModeChange = this.handleMainPurchaseModeChange.bind(this);
        this.handleResize = this.handleResize.bind(this);

        this.variantData = this.getVariantData();
        this.currentVariant = this.getVariantFromInput();
        this.blockVisibility = this.stickyEnabledMarker?.dataset?.stickyVisibility || 'all';
        this.customButtonLabel = this.stickyEnabledMarker?.dataset?.stickyButtonLabel?.trim() || '';

        this.init();
      }

      disableSticky() {
        this.container?.classList.remove('sticky-atc-bar--show');
        this.container?.setAttribute('hidden', 'hidden');
        document.documentElement.style.setProperty('--sticky-atc-bar-height', '0px');
      }

      enableSticky() {
        this.container?.removeAttribute('hidden');
        this.applyBlockVisibility();
      }

      applyBlockVisibility() {
        if (!this.container) return;

        this.container.classList.remove('hidden', 'block', 'md:hidden', 'md:block');
        switch (this.blockVisibility) {
          case 'desktop':
            this.container.classList.add('hidden', 'md:block');
            break;
          case 'mobile':
            this.container.classList.add('block', 'md:hidden');
            break;
          default:
            break;
        }
      }

      disconnectedCallback() {
        this.selectorButton?.removeEventListener('click', this.handleSelectorClick);
        this.dropdownOptions?.removeEventListener('click', this.handleDropdownOptionClick);
        this.productForm?.removeEventListener('submit', this.handleFormSubmit);
        this.mainProductInfo?.removeEventListener('change', this.handleMainPlanChange);
        this.mainProductInfo?.removeEventListener('pdp:purchase-mode-changed', this.handleMainPurchaseModeChange);
        document.removeEventListener('click', this.handleDocumentClick);
        document.removeEventListener('keydown', this.handleDocumentKeydown);
        this.variantChangeUnsubscriber?.();
        this.observer?.disconnect();
        this.closeDropdown();
        window.removeEventListener('resize', this.handleResize);
      }

      init() {
        this.selectorButton?.addEventListener('click', this.handleSelectorClick);
        this.dropdownOptions?.addEventListener('click', this.handleDropdownOptionClick);
        this.productForm.addEventListener('submit', this.handleFormSubmit);
        this.mainProductInfo?.addEventListener('change', this.handleMainPlanChange);
        this.mainProductInfo?.addEventListener('pdp:purchase-mode-changed', this.handleMainPurchaseModeChange);
        document.addEventListener('click', this.handleDocumentClick);
        document.addEventListener('keydown', this.handleDocumentKeydown);

        if (window.FoxTheme?.pubsub?.subscribe) {
          this.variantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
            ({ data }) => {
              if (!this.mainProductInfo) return;
              if (data.sectionId !== this.mainProductInfo.dataset.section) return;
              this.currentVariant = data.variant || null;
              if (this.variantInput && this.currentVariant?.id) {
                this.variantInput.value = String(this.currentVariant.id);
              }
              this.syncFromMain();
            }
          );
        }

        this.initObserver();
        this.syncFromMain();
        this.handleResize();
        window.addEventListener('resize', this.handleResize);
      }

      handleResize() {
        document.documentElement.style.setProperty('--sticky-atc-bar-height', `${this.clientHeight}px`);
      }

      initObserver() {
        this.observeTarget = this.mainAddToCartButton || this.mainNativeAtcButton || this.mainAddToCartBlock || null;

        if (!this.observeTarget) {
          this.container?.classList.remove('sticky-atc-bar--show');
          return;
        }

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const passedAtcButton = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
              this.container?.classList.toggle('sticky-atc-bar--show', passedAtcButton);
              if (!passedAtcButton) {
                this.closeDropdown();
              }
            });
          },
          { threshold: 0 }
        );
        this.observer.observe(this.observeTarget);
      }

      getVariantData() {
        const jsonNode = this.container?.querySelector('[type="application/json"]');
        if (!jsonNode) return [];
        try {
          return JSON.parse(jsonNode.textContent);
        } catch (error) {
          return [];
        }
      }

      getVariantFromInput() {
        if (!this.variantInput?.value) return null;
        return this.variantData.find((variant) => String(variant.id) === String(this.variantInput.value)) || null;
      }

      handleSelectorClick(event) {
        event.preventDefault();
        if (!this.dropdown || !this.dropdownOptions) return;

        const options = this.dropdownOptions.querySelectorAll('[data-sticky-option]');
        if (!options.length) return;

        if (this.dropdown.hasAttribute('hidden')) {
          this.openDropdown();
        } else {
          this.closeDropdown();
        }
      }

      handleDropdownOptionClick(event) {
        const optionButton = event.target.closest('[data-sticky-option]');
        if (!optionButton) return;

        event.preventDefault();

        const mode = optionButton.dataset.mode === 'subscription' ? 'subscription' : 'one-time';
        const planId = optionButton.dataset.planId || '';

        const changed = this.applySelectionToMain(mode, planId);
        if (!changed) return;

        this.closeDropdown();
        this.syncFromMain();
      }

      handleDocumentClick(event) {
        if (!this.dropdown || this.dropdown.hasAttribute('hidden')) return;
        if (this.contains(event.target)) return;
        this.closeDropdown();
      }

      handleDocumentKeydown(event) {
        if (event.key !== 'Escape') return;
        this.closeDropdown();
      }

      openDropdown() {
        if (!this.dropdown) return;
        this.dropdown.removeAttribute('hidden');
        this.selectorButton?.setAttribute('aria-expanded', 'true');
      }

      closeDropdown() {
        if (!this.dropdown) return;
        this.dropdown.setAttribute('hidden', 'hidden');
        this.selectorButton?.setAttribute('aria-expanded', 'false');
      }

      handleMainPlanChange(event) {
        if (!event.target.closest('.pdp-selling-plans__input')) return;
        this.syncFromMain();
      }

      handleMainPurchaseModeChange() {
        this.syncFromMain();
      }

      syncFromMain() {
        this.syncVariantFromMain();
        const selection = this.getCurrentPurchaseSelection();
        this.renderSelection(selection);
        this.renderDropdownOptions(selection);
        this.syncPlanInput(selection);
        this.syncValuesToMainForm();
        this.updateButtonState();
      }

      syncVariantFromMain() {
        if (!this.variantInput) return;

        const mainVariantInput =
          this.mainProductInfo?.querySelector('input.product-variant-id[name="id"]') ||
          this.mainProductInfo?.querySelector('form[is="product-form"] input[name="id"]');
        if (!mainVariantInput?.value) return;

        this.variantInput.value = mainVariantInput.value;
        this.currentVariant = this.variantData.find((variant) => String(variant.id) === String(mainVariantInput.value)) || null;
      }

      getCurrentPurchaseSelection() {
        const fallback = {
          mode: this.dataset.defaultMode || 'one-time',
          planId: this.dataset.defaultPlanId || '',
          titleMain: this.dataset.defaultTitleMain || 'Einmaliger Kauf',
          titleSave: this.dataset.defaultTitleSave || '',
          subtitle: this.dataset.defaultSubtitle || '',
        };

        if (!this.mainProductInfo) return fallback;

        const oneTimeRoot = this.mainProductInfo.querySelector('.pdp-one-time-purchase');
        const oneTimeButton = oneTimeRoot?.querySelector('.pdp-one-time-purchase__button');
        const selectedPlanInput = this.mainProductInfo.querySelector('.pdp-selling-plans__input:checked');

        const isOneTime =
          oneTimeButton?.classList.contains('is-selected') ||
          this.mainProductInfo.dataset.pdpPurchaseMode === 'one-time' ||
          !selectedPlanInput;

        if (!isOneTime && selectedPlanInput) {
          const planName = selectedPlanInput.dataset.planDisplayName?.trim() || fallback.titleMain;
          const saveText = selectedPlanInput.dataset.planSaveText?.trim() || '';
          const perDayValue = selectedPlanInput.dataset.planPerDay?.trim() || '';
          const subtitle = perDayValue ? `€${perDayValue} pro Tag` : fallback.subtitle;

          return {
            mode: 'subscription',
            planId: String(selectedPlanInput.value),
            titleMain: planName,
            titleSave: saveText,
            subtitle,
          };
        }

        const oneTimeLabel = oneTimeRoot?.dataset.oneTimeLabel?.trim() || fallback.titleMain;
        const oneTimePrice = oneTimeRoot?.dataset.oneTimePrice?.trim();
        const subtitle = oneTimePrice ? `€${oneTimePrice} einmalig` : fallback.subtitle;

        return {
          mode: 'one-time',
          planId: '',
          titleMain: oneTimeLabel,
          titleSave: '',
          subtitle,
        };
      }

      getAvailablePurchaseOptions() {
        if (!this.mainProductInfo) return [];

        const options = [];
        const planInputs = Array.from(this.mainProductInfo.querySelectorAll('.pdp-selling-plans__input'));
        planInputs.forEach((input) => {
          const planId = String(input.value || '');
          if (!planId) return;

          const titleMain = input.dataset.planDisplayName?.trim() || input.value;
          const titleSave = input.dataset.planSaveText?.trim() || '';
          const perDayValue = input.dataset.planPerDay?.trim() || '';
          const subtitle = perDayValue ? `€${perDayValue} pro Tag` : '';

          options.push({
            mode: 'subscription',
            planId,
            titleMain,
            titleSave,
            subtitle,
          });
        });

        const oneTimeRoot = this.mainProductInfo.querySelector('.pdp-one-time-purchase');
        if (oneTimeRoot) {
          const oneTimeLabel = oneTimeRoot.dataset.oneTimeLabel?.trim() || 'Einmaliger Kauf';
          const oneTimePrice = oneTimeRoot.dataset.oneTimePrice?.trim() || '';
          options.push({
            mode: 'one-time',
            planId: '',
            titleMain: oneTimeLabel,
            titleSave: '',
            subtitle: oneTimePrice ? `€${oneTimePrice} einmalig` : '',
          });
        }

        return options;
      }

      renderSelection(selection) {
        if (this.titleMainEl) {
          this.titleMainEl.textContent = selection.titleMain;
        }

        if (this.titleSaveEl) {
          this.titleSaveEl.textContent = selection.titleSave;
          this.titleSaveEl.classList.toggle('hidden', !selection.titleSave);
        }

        if (this.subtitleEl) {
          this.subtitleEl.textContent = selection.subtitle;
        }
      }

      renderDropdownOptions(selection) {
        if (!this.dropdownOptions) return;

        const options = this.getAvailablePurchaseOptions();
        if (!options.length) {
          this.dropdownOptions.innerHTML = '';
          this.selectorButton?.setAttribute('disabled', 'disabled');
          this.closeDropdown();
          return;
        }

        const selectedKey = `${selection.mode}:${selection.planId || ''}`;
        const optionMarkup = options
          .map((option) => {
            const optionKey = `${option.mode}:${option.planId || ''}`;
            const isSelected = optionKey === selectedKey;
            const saveClass = option.titleSave ? '' : ' hidden';
            const subtitle = this.escapeHtml(option.subtitle || '');
            const titleMain = this.escapeHtml(option.titleMain || '');
            const titleSave = this.escapeHtml(option.titleSave || '');
            const planId = this.escapeHtml(option.planId || '');

            return `
              <button
                type="button"
                class="sticky-atc-bar__dropdown-option${isSelected ? ' is-selected' : ''}"
                role="option"
                aria-selected="${isSelected ? 'true' : 'false'}"
                data-sticky-option
                data-mode="${option.mode}"
                data-plan-id="${planId}"
              >
                <span class="sticky-atc-bar__dropdown-option-radio" aria-hidden="true">
                  <span class="sticky-atc-bar__dropdown-option-radio-dot"></span>
                </span>
                <span class="sticky-atc-bar__dropdown-option-content">
                  <span class="sticky-atc-bar__dropdown-option-title">
                    <span class="sticky-atc-bar__dropdown-option-title-main">${titleMain}</span>
                    <span class="sticky-atc-bar__dropdown-option-title-save${saveClass}">${titleSave}</span>
                  </span>
                  <span class="sticky-atc-bar__dropdown-option-subtitle">${subtitle}</span>
                </span>
              </button>
            `;
          })
          .join('');

        this.dropdownOptions.innerHTML = optionMarkup;

        if (options.length <= 1) {
          this.selectorButton?.setAttribute('disabled', 'disabled');
          this.closeDropdown();
        } else {
          this.selectorButton?.removeAttribute('disabled');
        }
      }

      applySelectionToMain(mode, planId) {
        if (!this.mainProductInfo) return false;

        if (mode === 'one-time') {
          const oneTimeButton = this.mainProductInfo.querySelector('.pdp-one-time-purchase__button');
          if (oneTimeButton) {
            oneTimeButton.click();
            return true;
          }

          this.mainProductInfo.dataset.pdpPurchaseMode = 'one-time';
          this.mainProductInfo.dispatchEvent(
            new CustomEvent('pdp:purchase-mode-changed', {
              bubbles: true,
            })
          );
          return true;
        }

        const inputs = Array.from(this.mainProductInfo.querySelectorAll('.pdp-selling-plans__input'));
        const targetInput = inputs.find((input) => String(input.value) === String(planId));
        if (!targetInput) return false;

        targetInput.checked = true;
        targetInput.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }

      syncPlanInput(selection) {
        if (!this.sellingPlanInput) return;
        this.sellingPlanInput.value = selection.mode === 'subscription' ? selection.planId : '';
      }

      ensureMainFormPlanInput() {
        if (!this.mainProductForm) return null;
        let planInput = this.mainProductForm.querySelector('input[name="selling_plan"]');
        if (!planInput) {
          planInput = document.createElement('input');
          planInput.type = 'hidden';
          planInput.name = 'selling_plan';
          this.mainProductForm.appendChild(planInput);
        }
        return planInput;
      }

      syncValuesToMainForm() {
        if (!this.mainProductForm) return;

        const mainVariantInput = this.mainProductForm.querySelector('input[name="id"]');
        if (mainVariantInput && this.variantInput?.value) {
          mainVariantInput.value = this.variantInput.value;
        }

        const mainPlanInput = this.ensureMainFormPlanInput();
        if (mainPlanInput && this.sellingPlanInput) {
          mainPlanInput.value = this.sellingPlanInput.value || '';
        }
      }

      hasMainRequiredFields() {
        if (!this.mainProductInfo) return false;
        return this.mainProductInfo.querySelectorAll('[data-custom-property]').length > 0;
      }

      handleFormSubmit(event) {
        this.syncFromMain();

        if (!this.hasMainRequiredFields()) return;
        if (!this.mainProductForm) return;

        event.preventDefault();
        event.stopPropagation();

        this.mainProductForm.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => {
          this.mainProductForm.requestSubmit();
        }, 220);
      }

      updateButtonState() {
        if (!this.submitButton) return;

        const variant = this.currentVariant || this.getVariantFromInput();
        const disable = !variant || !variant.available;
        this.submitButton.toggleAttribute('disabled', disable);

        if (!this.submitButtonText) return;
        if (disable) {
          this.submitButtonText.textContent = window.FoxTheme?.variantStrings?.soldOut || this.submitButtonText.textContent;
          return;
        }
        if (this.customButtonLabel) {
          this.submitButtonText.textContent = this.customButtonLabel;
        } else {
          this.submitButtonText.textContent = window.FoxTheme?.variantStrings?.addToCart || this.submitButtonText.textContent;
        }
      }

      escapeHtml(value) {
        return String(value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');
      }
    }
  );
}
